import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "radix-ui";
import { Button, Section, cn, dialogBodyClass, dialogContentClass, dialogOverlayClass, useAuth } from "@ozpanel/ui";

// ConnectClaude — hands a person what they need to manage their part of the panel from Claude: a token made
// for it, and the lines to paste. The same component in all three consoles; only the audience differs, and
// the audience decides the presets (an operator's "full control" is the platform, an account owner's is
// their account, a mailbox user's is their mailbox — the token can never exceed its owner either way).
//
// The token is an ordinary API token (POST /auth/tokens), so it is listed, revocable and audited like any
// other; the MCP endpoint refuses console sessions, so this token IS the connection. Every line below
// carries the token, and is shown exactly once — the same rule as every other secret here.

export type ClaudeAudience = "operator" | "account" | "mailbox";

interface Preset {
  id: string;
  label: string;
  help: string;
  scopes: string[] | "custom";
  dangerous: boolean;
}

// How long a token lives is the issuer's choice, destructive presets included, down to "never — until
// revoked" (Oz, 2026-11-04: "time cap should be configured by the user issuing … can be unlimited and only
// manually revoked"). NEVER is hours = 0: no expires_at is sent, and the token lives until it is revoked.
const NEVER = 0;

const PRESETS: Record<ClaudeAudience, Preset[]> = {
  operator: [
    { id: "full", label: "Full control", scopes: ["*"], dangerous: true,
      help: "Everything you can do, including suspending and deleting accounts, provisioning and other destructive actions. You choose how long it lasts." },
    { id: "manage", label: "Manage", scopes: ["*"], dangerous: false,
      help: "Create and change accounts, plans, logins, domains and settings. Destructive actions are refused." },
    { id: "read", label: "Read only", scopes: ["*:read"], dangerous: false,
      help: "Look at everything — accounts, health, alerts, settings — and change nothing." },
    { id: "custom", label: "Custom", scopes: "custom", dangerous: false, help: "Choose the areas yourself." },
  ],
  account: [
    { id: "full", label: "Full account", scopes: ["*"], dangerous: true,
      help: "Everything in this account, including stopping and deleting apps, dropping databases and restoring backups. You choose how long it lasts." },
    { id: "manage", label: "Everyday", scopes: ["*"], dangerous: false,
      help: "Domains, DNS, websites and apps, databases, mail, files, cron, Redis and backups. Destructive actions are refused." },
    { id: "read", label: "Read only", scopes: ["*:read"], dangerous: false, help: "Look at everything in the account and change nothing." },
    { id: "custom", label: "Custom", scopes: "custom", dangerous: false, help: "Choose the areas yourself." },
  ],
  mailbox: [
    { id: "manage", label: "My mailbox", scopes: ["*"], dangerous: false,
      help: "Mail rules, out-of-office reply, spam settings and sender lists, app passwords, imports and notifications." },
    { id: "read", label: "Read only", scopes: ["*:read"], dangerous: false, help: "Look at your mailbox's settings and statistics, change nothing." },
  ],
};

// The account's areas, by the API tag that gates them. Ticked = may change (and read); the last box adds
// read access to everything else.
const ACCOUNT_AREAS: [string, string][] = [
  ["domains", "Domains"], ["dns", "DNS records"], ["dns-hosting", "DNS hosting"], ["caddy", "Websites"],
  ["apps", "Apps"], ["databases", "Databases"], ["mail", "Mail"], ["files", "Files"], ["cron", "Cron jobs"],
  ["redis", "Redis"], ["backups", "Backups"], ["tenants", "Account and usage"],
];
const OPERATOR_AREAS: [string, string][] = [
  ["tenants", "Accounts"], ["plans", "Plans"], ["domains", "Domains"], ["dns", "DNS"], ["mail", "Mail"],
  ["apps", "Apps"], ["caddy", "Websites"], ["backups", "Backups"], ["cluster", "Cluster"], ["ozpanel", "Panel health and alerts"],
];

const EXPIRY: { hours: number; label: string }[] = [
  { hours: 12, label: "12 hours" },
  { hours: 24 * 7, label: "7 days" },
  { hours: 24 * 30, label: "30 days" },
  { hours: 24 * 90, label: "90 days" },
  { hours: 24 * 365, label: "1 year" },
  { hours: NEVER, label: "Never — until I revoke it" },
];

/** The presets an audience is offered — exported for the test that holds the safety rules. */
export function claudePresets(audience: ClaudeAudience): Preset[] {
  return PRESETS[audience];
}

export interface ClaudeTokenChoice {
  presetId: string;
  picked?: Record<string, boolean>; // custom: areas that may change
  readRest?: boolean; // custom: read everything else
  hours: number; // 0 = never expires (until revoked)
  ips?: string;
  name?: string;
}

/** The POST /auth/tokens body for a choice. ONE place decides the scopes, the dangerous flag and the
 *  expiry, so the dialog and its test cannot disagree. */
export function claudeTokenRequest(audience: ClaudeAudience, c: ClaudeTokenChoice, now = Date.now()) {
  const presets = PRESETS[audience];
  const preset = (presets.find((p) => p.id === c.presetId) ?? presets[0]) as Preset;
  const areas = audience === "operator" ? OPERATOR_AREAS : ACCOUNT_AREAS;
  const hours = c.hours > 0 ? c.hours : NEVER;
  const scopes: string[] =
    preset.scopes === "custom"
      ? [...areas.filter(([tag]) => c.picked?.[tag]).map(([tag]) => `${tag}:write`), ...(c.readRest ? ["*:read"] : [])]
      : preset.scopes;
  return {
    preset,
    hours,
    body: {
      name: (c.name ?? "").trim() || "Claude",
      scopes,
      allow_dangerous: preset.dangerous,
      // Omitted for "never": the server stores no expiry and the token lives until it is revoked.
      ...(hours === NEVER ? {} : { expires_at: new Date(now + hours * 3600_000).toISOString() }),
      allowed_ips: (c.ips ?? "").split(/[\s,]+/).map((x) => x.trim()).filter(Boolean),
    },
  };
}

const SKILL_DIR: Record<ClaudeAudience, string> = { operator: "ozpanel-operator", account: "ozpanel-account", mailbox: "ozpanel-mailbox" };

export function ConnectClaude({ audience }: { audience: ClaudeAudience }) {
  const [open, setOpen] = React.useState(false);
  const what = {
    operator: "the platform — accounts, plans, logins, domains and the cluster",
    account: "this hosting account — domains, DNS, websites and apps, databases, mail, files and backups",
    mailbox: "your mailbox — rules, out-of-office, spam settings and app passwords",
  }[audience];
  return (
    <Section
      title="Connect Claude"
      actions={
        <Button variant="accent" size="sm" onClick={() => setOpen(true)}>
          Connect Claude
        </Button>
      }
    >
      <div className="max-w-2xl px-1 pb-2 text-[12.5px] leading-relaxed text-muted">
        Manage {what} by asking Claude. You choose what it may do and for how long. For Claude Code, the
        button gives you a token made for it and the lines to paste. Claude can never do more than you can, every
        change it makes is in the audit trail under the token's name, and revoking the token below cuts it off at
        once.
      </div>
      <div className="max-w-2xl px-1 pb-2 text-[12.5px] leading-relaxed text-muted">
        In <b>claude.ai</b>, <b>Claude Desktop</b> or the <b>Claude app</b>: Settings → Connectors → Add custom
        connector, with this address — you then sign in here and choose what it may do, no token to copy:
        <div className="mt-1 flex items-center gap-2">
          <code className="break-all rounded border border-line bg-bg2 px-2 py-1 font-mono text-[12px] select-all">
            {window.location.origin}/api/v1/mcp
          </code>
          <CopyButton text={`${window.location.origin}/api/v1/mcp`} />
        </div>
      </div>
      {open && <ConnectDialog audience={audience} onClose={() => setOpen(false)} />}
    </Section>
  );
}

// The choice of what Claude may do and for how long — ONE form, used by the Connect Claude dialog (a token
// and lines to paste) and by the connector consent screen (a sign-in from claude.ai), so the two cannot
// offer different things.
function useAccessChoice(audience: ClaudeAudience, defaultName: string, opts: { askAddresses?: boolean } = {}) {
  const presets = PRESETS[audience];
  const [presetId, setPresetId] = React.useState(presets.find((p) => !p.dangerous)?.id ?? presets[0]?.id ?? "");
  const areas = audience === "operator" ? OPERATOR_AREAS : ACCOUNT_AREAS;
  const [picked, setPicked] = React.useState<Record<string, boolean>>({});
  const [readRest, setReadRest] = React.useState(true);
  const [hours, setHours] = React.useState(audience === "mailbox" ? 24 * 30 : audience === "account" ? 24 * 7 : 12);
  const [ips, setIps] = React.useState("");
  const [name, setName] = React.useState(defaultName);
  const choice: ClaudeTokenChoice = { presetId, picked, readRest, hours, ips, name };
  const req = claudeTokenRequest(audience, choice);
  const { preset, hours: effHours } = req;
  const fields = (
    <>
      <div className="mt-3 space-y-2">
        {presets.map((p) => (
          <label
            key={p.id}
            className={cn(
              "flex cursor-pointer gap-3 rounded border px-3 py-2",
              p.id === presetId ? "border-[var(--ozp-accent-line)] bg-bg2" : "border-line",
            )}
          >
            <input type="radio" name="claude-preset" className="mt-1" checked={p.id === presetId} onChange={() => setPresetId(p.id)} />
            <span>
              <span className="block text-[13px] font-medium">{p.label}</span>
              <span className="block text-[12px] leading-relaxed text-muted">{p.help}</span>
            </span>
          </label>
        ))}
      </div>
      {preset.scopes === "custom" && (
        <div className="mt-3 rounded border border-line px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-muted">May change</div>
          <div className="mt-1 grid grid-cols-2 gap-1 sm:grid-cols-3">
            {areas.map(([tag, label]) => (
              <label key={tag} className="flex items-center gap-2 text-[12.5px]">
                <input type="checkbox" checked={!!picked[tag]} onChange={(e) => setPicked({ ...picked, [tag]: e.target.checked })} />
                {label}
              </label>
            ))}
          </div>
          <label className="mt-2 flex items-center gap-2 text-[12.5px]">
            <input type="checkbox" checked={readRest} onChange={(e) => setReadRest(e.target.checked)} />
            May read everything else
          </label>
        </div>
      )}
      {preset.dangerous && (
        <div className="mt-3 text-[12px] leading-relaxed text-warn">
          Claude will be able to run destructive actions without a further confirmation from the panel. It is
          told to do so only when you ask for that specific thing — but the token allows it.
        </div>
      )}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-[12px]">
          Expires after
          <select
            className="input mt-1 w-full"
            value={effHours}
            onChange={(e) => setHours(Number(e.target.value))}
          >
            {EXPIRY.map((x) => (
              <option key={x.hours} value={x.hours}>
                {x.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-[12px]">
          Name in your token list
          <input className="input mt-1 w-full" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
      </div>
      {opts.askAddresses !== false && (
        <label className="mt-3 block text-[12px]">
          Only from these addresses (optional — the public IP of the machine Claude runs on; comma separated)
          <input className="input mt-1 w-full" placeholder="any address" value={ips} onChange={(e) => setIps(e.target.value)} />
        </label>
      )}
    </>
  );
  return { choice, req, fields };
}

function ConnectDialog({ audience, onClose }: { audience: ClaudeAudience; onClose: () => void }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { choice, req, fields } = useAccessChoice(audience, "Claude");
  const { preset, hours: effHours } = req;
  const scopes = req.body.scopes;
  const [err, setErr] = React.useState<string | null>(null);
  const [token, setToken] = React.useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      client.apiFetch<{ token: string }>("/auth/tokens", {
        method: "POST",
        // Rebuilt at the moment of sending, so the expiry counts from the click, not from the render.
        body: JSON.stringify(claudeTokenRequest(audience, choice).body),
      }),
    onSuccess: (r: any) => {
      const v = r?.data?.token ?? r?.token ?? "";
      if (!v) {
        setErr("The panel answered without a token — nothing to connect with. Check the API tokens list and revoke any new entry.");
        return;
      }
      setToken(v);
      void qc.invalidateQueries({ queryKey: ["my-api-tokens"] });
      void qc.invalidateQueries({ queryKey: ["auth-tokens"] });
      void qc.invalidateQueries({ queryKey: ["my-tokens"] });
    },
    onError: (e: any) => setErr(String(e?.message ?? e)),
  });

  return (
    <Dialog.Root open onOpenChange={() => {}}>
      <Dialog.Portal>
        <Dialog.Overlay className={dialogOverlayClass} />
        <Dialog.Content
          className={cn(dialogContentClass, "max-w-2xl")}
          onEscapeKeyDown={(e) => token && e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className={cn(dialogBodyClass, "max-h-[80vh] overflow-y-auto p-5")}>
            <Dialog.Title className="text-[15px] font-medium">
              {token ? "Paste this into Claude" : "Connect Claude"}
            </Dialog.Title>
            {token ? (
              <ConnectLines audience={audience} token={token} dangerous={preset.dangerous} hours={effHours} />
            ) : (
              <>
                <Dialog.Description className="mt-2 text-[12.5px] leading-relaxed text-muted">
                  What may Claude do?
                </Dialog.Description>
                {fields}
                {err && <div className="mt-3 text-[12px] text-bad">{err}</div>}
              </>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3.5">
            {token ? (
              <Button variant="accent" onClick={onClose}>
                I have copied it
              </Button>
            ) : (
              <>
                <Button variant="default" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="accent"
                  disabled={create.isPending || scopes.length === 0}
                  onClick={() => {
                    setErr(null);
                    create.mutate();
                  }}
                >
                  {create.isPending ? "Creating…" : "Create the connection"}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ConnectLines({ audience, token, dangerous, hours }: { audience: ClaudeAudience; token: string; dangerous: boolean; hours: number }) {
  const origin = window.location.origin;
  const mcp = `${origin}/api/v1/mcp`;
  const dir = `~/.claude/skills/${SKILL_DIR[audience]}`;
  const addLine = `claude mcp add --transport http --scope user ozpanel ${mcp} --header "Authorization: Bearer ${token}"`;
  const skillLine = `mkdir -p ${dir} && curl -fsS -H "Authorization: Bearer ${token}" "${origin}/api/v1/mcp/guide?format=skill" -o ${dir}/SKILL.md`;
  const desktop = JSON.stringify(
    { mcpServers: { ozpanel: { command: "npx", args: ["-y", "mcp-remote", mcp, "--header", `Authorization: Bearer ${token}`] } } },
    null,
    2,
  );
  const first = {
    operator: "Use the ozpanel tools: run whoami, read the guide, then show me the cluster's health and any open alerts.",
    account: "Use the ozpanel tools: run whoami, read the guide, then give me an overview of my account — domains, apps, databases and usage against my plan.",
    mailbox: "Use the ozpanel tools: run whoami, read the guide, then show me my mailbox's usage and my current mail rules.",
  }[audience];
  const lifetime =
    hours === NEVER ? "until you revoke it" : hours >= 24 ? `for ${Math.round(hours / 24)} days` : `for ${hours} hours`;
  return (
    <div className="mt-2 space-y-4">
      <div className="text-[12.5px] leading-relaxed text-muted">
        These lines contain the token. They are shown <b>once</b>: copy what you need now. Keep them out of
        repositories and chats. The connection works {lifetime}
        {dangerous ? ", destructive actions included" : ""}; revoke it any time from your API tokens.
      </div>
      <CopyBlock
        step="1"
        title="Connect Claude Code"
        help="Run in a terminal. Every Claude Code session on that machine can then use the panel."
        text={addLine}
      />
      <CopyBlock
        step="2"
        title="Add the working guide as a skill (optional)"
        help="Teaches Claude how this panel works — the order to do things in and the rules — before it is connected."
        text={skillLine}
      />
      <CopyBlock step="3" title="Then ask" help="Paste as your first message in a new Claude Code session." text={first} />
      <details className="rounded border border-line px-3 py-2">
        <summary className="cursor-pointer text-[12.5px]">Claude Desktop or another MCP client</summary>
        <div className="mt-2">
          <CopyBlock
            title="claude_desktop_config.json → mcpServers"
            help="Uses mcp-remote (needs Node.js) to carry the token. Restart Claude Desktop after saving."
            text={desktop}
          />
        </div>
      </details>
    </div>
  );
}

function CopyBlock({ step, title, help, text }: { step?: string; title: string; help: string; text: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked (insecure context) — the text is still selectable by hand */
    }
  };
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[13px] font-medium">
          {step && <span className="mr-1.5 text-muted">{step}.</span>}
          {title}
        </div>
        <Button variant="default" size="sm" onClick={() => void copy()}>
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div className="text-[12px] leading-relaxed text-muted">{help}</div>
      <pre className="mt-1 whitespace-pre-wrap break-all rounded border border-line bg-bg2 px-3 py-2 font-mono text-[12px] select-all">
        {text}
      </pre>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      variant="default"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard?.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* clipboard blocked — the address is selectable by hand */
        }
      }}
    >
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

/** True when this page is a connector sign-in (…/connect-claude?req=…) — each console checks it before its
 *  role gate, so the consent screen works in whichever console the panel's sign-in sent the person to. */
export function isConnectClaudePath(): boolean {
  return /\/connect-claude\/?$/.test(window.location.pathname) && new URLSearchParams(window.location.search).has("req");
}

interface PendingInfo {
  client_name: string;
  redirect_host: string;
  audience: ClaudeAudience;
  login: string;
  deny_redirect: string;
}

/**
 * ConnectClaudeConsent — the screen a person reaches from claude.ai's "Add custom connector": sign in (the
 * console's own sign-in form, 2FA included), see WHICH client is asking and WHERE it will be sent back, choose
 * what Claude may do and for how long, and approve or decline. The panel then issues an ordinary API token and
 * hands it to the client through a short-lived code (server: mcp_oauth.go).
 */
export function ConnectClaudeConsent({ signIn }: { signIn: React.ReactNode }) {
  const { who, loading } = useAuth();
  if (loading) {
    return <div className="grid min-h-dvh place-items-center text-[13px] text-muted">Loading…</div>;
  }
  if (!who) return <>{signIn}</>;
  return <ConsentCard />;
}

function ConsentCard() {
  const { client } = useAuth();
  const req = new URLSearchParams(window.location.search).get("req") ?? "";
  const [info, setInfo] = React.useState<PendingInfo | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    client
      .apiFetch<PendingInfo>(`/mcp/oauth/request?req=${encodeURIComponent(req)}`)
      .then(setInfo)
      .catch((e: any) => setErr(String(e?.message ?? e)));
  }, [client, req]);
  return (
    <div className="grid min-h-dvh place-items-center bg-bg p-4">
      <div className="w-full max-w-xl rounded-lg border border-line bg-surface p-5">
        {err && !info && (
          <>
            <div className="text-[15px] font-medium">This sign-in cannot continue</div>
            <div className="mt-2 text-[12.5px] leading-relaxed text-bad">{err}</div>
            <div className="mt-2 text-[12.5px] text-muted">Start again from Claude's connector settings.</div>
          </>
        )}
        {!info && !err && <div className="text-[13px] text-muted">Loading…</div>}
        {info && <ConsentForm info={info} req={req} />}
      </div>
    </div>
  );
}

function ConsentForm({ info, req }: { info: PendingInfo; req: string }) {
  const { client, logout } = useAuth();
  const { choice, req: tokenReq, fields } = useAccessChoice(info.audience, `Claude (${info.client_name})`, { askAddresses: false });
  const [err, setErr] = React.useState<string | null>(null);
  const approve = useMutation({
    mutationFn: () =>
      client.apiFetch<{ redirect: string }>("/mcp/oauth/approve", {
        method: "POST",
        body: JSON.stringify({ req, ...claudeTokenRequest(info.audience, choice).body }),
      }),
    onSuccess: (r: any) => {
      const to = r?.redirect ?? r?.data?.redirect;
      if (to) window.location.assign(to);
      else setErr("The panel approved but did not say where to return. Check your token list and revoke the new entry.");
    },
    onError: (e: any) => setErr(String(e?.message ?? e)),
  });
  return (
    <>
      <div className="text-[15px] font-medium">Connect {info.client_name} to ozpanel</div>
      <div className="mt-2 text-[12.5px] leading-relaxed text-muted">
        <b className="text-fg">{info.client_name}</b> is asking to act as <b className="text-fg">{info.login}</b>. When you
        approve, you are sent back to <b className="text-fg">{info.redirect_host}</b> — if that is not where you started
        adding a connector, decline. It can never do more than you can; everything it changes is in the audit trail, and
        you can revoke it any time from your API tokens.
      </div>
      <div className="mt-3 text-[12.5px] font-medium">What may it do?</div>
      {fields}
      {err && <div className="mt-3 text-[12px] text-bad">{err}</div>}
      <div className="mt-4 flex flex-wrap justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => void logout()}>
          Not {info.login}? Sign out
        </Button>
        <div className="flex gap-2">
          <Button variant="default" onClick={() => window.location.assign(info.deny_redirect)}>
            Decline
          </Button>
          <Button
            variant="accent"
            disabled={approve.isPending || tokenReq.body.scopes.length === 0}
            onClick={() => {
              setErr(null);
              approve.mutate();
            }}
          >
            {approve.isPending ? "Connecting…" : "Approve"}
          </Button>
        </div>
      </div>
    </>
  );
}
